'use client'

import { useEffect, useState } from 'react'
import { usePathname } from 'next/navigation'
import { ChevronRight, Coins, Loader2, Medal, Ticket } from 'lucide-react'
import { useAuth } from '@/lib/commerce/hooks'
import { getLoyaltySummaryApi, getLoyaltyTransactionsApi, getLoyaltyVouchersApi } from '@/lib/commerce/services/loyalty-api'
import { navigateUrl } from '@/utils/client-request'
import { classNames, formatImageURL, formatNumber, formatPrice, formatDateTimeNormal } from '@/utils/util'
import { TRANSACTION_TYPE_LABELS, VOUCHER_STATUS_LABELS } from '@/components/templates/default/loyalty/labels'
import ConvertVoucherModal from '../loyalty/ConvertVoucherModal'

const PAGE_SIZE = 20

const VOUCHER_STATUS_STYLES: Record<string, React.CSSProperties> = {
  active: { background: 'rgba(16,185,129,0.14)', color: '#34d399', border: '1px solid rgba(16,185,129,0.3)' },
  used: { background: 'rgba(244,63,94,0.14)', color: '#fda4af', border: '1px solid rgba(244,63,94,0.3)' },
  expired: { background: 'rgba(245,158,11,0.14)', color: '#fbbf24', border: '1px solid rgba(245,158,11,0.3)' },
  canceled: { background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.55)', border: '1px solid rgba(255,255,255,0.13)' },
}

export default function ProfileLoyalty({ vendor }: { vendor: any }) {
  const pathname = usePathname()
  const { session, isLoggedIn } = useAuth()

  const [summary, setSummary] = useState<any>(null)
  const [vouchers, setVouchers] = useState<any[]>([])
  const [transactions, setTransactions] = useState<any[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [isLoading, setIsLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [convertOpen, setConvertOpen] = useState(false)
  const [refreshKey, setRefreshKey] = useState(0)

  const ctx = { vendorId: vendor?.id, session: session || undefined }

  useEffect(() => {
    if (!isLoggedIn) return
    let cancelled = false
    Promise.all([
      getLoyaltySummaryApi(ctx),
      getLoyaltyVouchersApi(ctx),
      getLoyaltyTransactionsApi(ctx, 1, PAGE_SIZE),
    ]).then(([summaryRes, vouchersRes, transactionsRes]) => {
      if (cancelled) return
      setSummary(summaryRes?.data || null)
      setVouchers(Array.isArray(vouchersRes?.data) ? vouchersRes.data : [])
      setTransactions(transactionsRes?.data?.transactions || [])
      setTotal(transactionsRes?.data?.total || 0)
    }).catch(() => {}).finally(() => { if (!cancelled) setIsLoading(false) })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, refreshKey])

  const loadMore = async () => {
    setLoadingMore(true)
    const nextPage = page + 1
    const { data } = await getLoyaltyTransactionsApi(ctx, nextPage, PAGE_SIZE)
    setLoadingMore(false)
    if (!data?.transactions) return
    setTransactions((prev) => [...prev, ...data.transactions])
    setTotal(data.total || 0)
    setPage(nextPage)
  }

  if (isLoading) {
    return (
      <div className="mx-auto w-full max-w-[1280px] px-4 lg:px-8 pt-10 pb-16">
        <div className="flex flex-col gap-y-5">
          <div className="flex flex-col gap-1.5">
            <div className="h-6 w-40 rounded animate-pulse" style={{ background: 'rgba(255,255,255,0.06)' }} />
            <div className="h-4 w-72 rounded animate-pulse" style={{ background: 'rgba(255,255,255,0.04)' }} />
          </div>
          <div className="h-24 w-full rounded-2xl animate-pulse" style={{ background: 'rgba(255,255,255,0.04)' }} />
          <div className="flex flex-col gap-2 mt-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-14 w-full rounded-xl animate-pulse" style={{ background: 'rgba(255,255,255,0.04)' }} />
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 lg:px-8 pt-10 pb-16">
      <div className="flex flex-col gap-y-5">
        <div className="flex flex-col gap-1">
          <div className="text-[18px] font-semibold text-white" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}>Лояалти оноо</div>
          <div className="text-[13px]" style={{ color: 'rgba(255,255,255,0.55)' }}>Таны цуглуулсан оноо болон онооны түүх</div>
        </div>

        {summary && (
          <div className="rounded-2xl p-5 flex flex-wrap items-center justify-between gap-4" style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}>
            <div className="flex items-center gap-3">
              {summary.currentTier?.icon ? (
                <img src={formatImageURL(summary.currentTier.icon)} alt={summary.currentTier.name} className="size-11 rounded-full object-cover" style={{ border: '1px solid rgba(255,255,255,0.13)' }} />
              ) : (
                <div className="size-11 rounded-full flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.07)' }}><Medal className="size-5" style={{ color: 'rgba(255,255,255,0.55)' }} /></div>
              )}
              <div>
                <div className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: '#a8a8b3' }}>Таны түвшин</div>
                <div className="font-semibold text-white mt-0.5" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}>{summary.currentTier?.name || '—'}</div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: '#a8a8b3' }}>Онооны үлдэгдэл</div>
              <div className="text-xl font-bold tabular-nums text-white mt-0.5 flex items-center gap-1.5 justify-end" style={{ fontFamily: 'Gilroy, Inter, sans-serif' }}><Coins className="size-4" style={{ color: 'rgba(255,255,255,0.55)' }} />{formatNumber(summary.balance || 0)}</div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <button type="button" onClick={() => setConvertOpen(true)} disabled={(summary.balance || 0) < (summary.minPointsToConvert || 1)} className="h-9 px-4 rounded-full text-[12.5px] font-semibold inline-flex items-center gap-1.5 transition-opacity hover:opacity-90 disabled:opacity-50 cursor-pointer disabled:cursor-not-allowed" style={{ background: 'white', color: '#0a0a0c' }}>
                <Ticket className="size-3.5" /> Ваучер болгох
              </button>
              <a href={navigateUrl('/loyalty', pathname)} className="text-[13px] font-medium text-white flex items-center gap-0.5 hover:opacity-70 transition-opacity">
                Хөтөлбөр үзэх
                <ChevronRight className="size-4" />
              </a>
            </div>
          </div>
        )}

        {vouchers.length > 0 && (
          <div className="rounded-2xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}>
            <div className="px-5 py-4 text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: '#a8a8b3' }}>Миний ваучерууд</div>
            {vouchers.map((voucher: any) => (
              <div key={voucher.id} className={classNames('flex items-center justify-between px-5 py-3.5', voucher.status !== 'active' && 'opacity-75')} style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                <div className="flex items-center gap-3 min-w-0">
                  <Ticket className="size-5 shrink-0" style={{ color: voucher.status === 'active' ? 'white' : 'rgba(255,255,255,0.4)' }} />
                  <div className="min-w-0">
                    <div className="font-medium font-mono text-sm text-white truncate">{voucher.code}</div>
                    <div className="text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>{voucher.expireAt ? `${formatDateTimeNormal(voucher.expireAt)} хүртэл` : 'Хугацаагүй'}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className={classNames('font-semibold tabular-nums', voucher.status !== 'active' && 'line-through')} style={{ color: voucher.status === 'active' ? 'white' : 'rgba(255,255,255,0.5)' }}>{formatPrice(Number(voucher.remainingAmount ?? voucher.amount) || 0)}</div>
                  <span className="inline-block text-[10px] font-semibold uppercase tracking-[0.14em] px-2 py-0.5 rounded-full mt-1" style={VOUCHER_STATUS_STYLES[voucher.status] || VOUCHER_STATUS_STYLES.canceled}>{VOUCHER_STATUS_LABELS[voucher.status] || voucher.status}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {transactions.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-3 py-20" style={{ color: 'rgba(255,255,255,0.5)' }}>
            <Medal className="h-8 w-8" />
            <div className="text-center">
              <div className="text-[14px]">Онооны түүх хоосон байна</div>
              <div className="text-[12.5px] mt-1" style={{ color: 'rgba(255,255,255,0.4)' }}>Худалдан авалт хийж оноо цуглуулж эхлээрэй.</div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            <div className="rounded-2xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.035)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <div className="px-5 py-4 text-[10px] font-semibold uppercase tracking-[0.22em]" style={{ color: '#a8a8b3' }}>Онооны түүх</div>
              {transactions.map((transaction: any) => (
                <div key={transaction.id} className="flex items-center justify-between px-5 py-3.5" style={{ borderTop: '1px solid rgba(255,255,255,0.07)' }}>
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-white">{TRANSACTION_TYPE_LABELS[transaction.type] || transaction.type}</div>
                    <div className="text-xs truncate" style={{ color: 'rgba(255,255,255,0.5)' }}>{formatDateTimeNormal(transaction.createdAt)}{transaction.description ? ` - ${transaction.description}` : ''}</div>
                  </div>
                  <div className="font-semibold tabular-nums shrink-0 pl-3" style={{ color: transaction.points > 0 ? '#34d399' : transaction.points < 0 ? '#fda4af' : 'white' }}>
                    {transaction.points > 0 ? '+' : ''}{formatNumber(transaction.points)}
                  </div>
                </div>
              ))}
            </div>
            {transactions.length < total && (
              <button type="button" onClick={loadMore} disabled={loadingMore} className="self-center h-10 px-6 rounded-full text-[12.5px] font-medium inline-flex items-center gap-2 transition-colors hover:bg-white/5 disabled:opacity-60 cursor-pointer" style={{ border: '1px solid rgba(255,255,255,0.2)', color: 'white' }}>
                {loadingMore && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                Цааш үзэх
              </button>
            )}
          </div>
        )}
      </div>

      <ConvertVoucherModal open={convertOpen} setOpen={setConvertOpen} vendor={vendor} program={summary} balance={summary?.balance || 0} onConverted={() => setRefreshKey((key) => key + 1)} />
    </div>
  )
}
